import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Clapperboard, Film } from 'lucide-react';
import type { PortfolioItem } from '../../services/portfolio';
import type { Category } from '../../services/categories';
import { getCategoryAccent } from '../../lib/category-theme';
import { countMediaTiles, groupTilesByCategory, type PortfolioMediaTile } from '../../lib/portfolio-tiles';
import PortfolioCarouselRow from './PortfolioCarouselRow';
import PortfolioCategoryAlbum from './PortfolioCategoryAlbum';

type Props = {
  items: PortfolioItem[];
  categories: Category[];
  imgSrc: (url?: string) => string;
  onOpenTile: (tile: PortfolioMediaTile) => void;
  filterCategoryId?: string | null;
  maxRows?: number;
  compact?: boolean;
  showHero?: boolean;
  heroTitle?: string;
  heroSubtitle?: string;
};

export default function PortfolioNetflixGallery({
  items,
  categories,
  imgSrc,
  onOpenTile,
  filterCategoryId,
  maxRows,
  compact,
  showHero = true,
  heroTitle = 'Nos Réalisations Photo & Vidéo',
  heroSubtitle = 'L Benna Production · Depuis 1988',
}: Props) {
  const groups = useMemo(
    () => groupTilesByCategory(items, categories),
    [items, categories],
  );

  const filteredGroup = useMemo(() => {
    if (!filterCategoryId || filterCategoryId === 'all') return null;
    return groups.find((g) => g.id === filterCategoryId) ?? null;
  }, [groups, filterCategoryId]);

  const visibleGroups = maxRows ? groups.slice(0, maxRows) : groups;
  const mediaCount = useMemo(() => countMediaTiles(items), [items]);
  const heroImage = items[0]?.coverUrl ? imgSrc(items[0].coverUrl) : undefined;

  if (groups.length === 0) {
    return (
      <div className="py-24 text-center text-zinc-500">
        <Film className="mx-auto mb-4 h-12 w-12 opacity-30" />
        <p className="text-sm font-bold uppercase tracking-widest">Aucune réalisation pour le moment</p>
      </div>
    );
  }

  if (filteredGroup) {
    return (
      <PortfolioCategoryAlbum
        title={filteredGroup.name}
        slug={filteredGroup.slug}
        tiles={filteredGroup.tiles}
        accent={getCategoryAccent(filteredGroup.accentIndex)}
        imgSrc={imgSrc}
        onOpenTile={onOpenTile}
      />
    );
  }

  return (
    <div className={compact ? '' : 'pb-16'}>
      {showHero && (
        <section className="relative overflow-hidden pb-8 pt-20 md:pt-24">
          {heroImage && (
            <>
              <img
                src={heroImage}
                alt=""
                className="absolute inset-0 h-full w-full scale-105 object-cover opacity-30 blur-sm"
                aria-hidden
              />
              <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/40 via-zinc-950/85 to-zinc-950" />
            </>
          )}
          {!heroImage && (
            <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 to-zinc-950" />
          )}

          <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
            <p className="mb-3 text-[10px] font-black uppercase tracking-[0.3em] text-primary-400">
              {heroSubtitle}
            </p>
            <h1 className="mb-4 text-3xl font-black tracking-tight text-white md:text-5xl">
              {heroTitle || 'Nos Réalisations Photo & Vidéo'}
            </h1>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm font-bold text-zinc-300">
              <span className="inline-flex items-center gap-2">
                <Camera className="h-4 w-4 text-primary-400" />
                {mediaCount} Médias
              </span>
              <span className="inline-flex items-center gap-2">
                <Clapperboard className="h-4 w-4 text-primary-400" />
                {groups.length} Catégories
              </span>
            </div>
          </div>
        </section>
      )}

      <div className="space-y-8 md:space-y-10">
        {visibleGroups.map((group) => (
          <PortfolioCarouselRow
            key={group.id}
            title={group.name}
            slug={group.slug}
            categoryId={group.id}
            tiles={group.tiles}
            accent={getCategoryAccent(group.accentIndex)}
            imgSrc={imgSrc}
            onOpenTile={onOpenTile}
            compact={compact}
          />
        ))}
      </div>

      {compact && groups.length > (maxRows ?? 0) && (
        <div className="mt-10 text-center">
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-primary-500/10 px-8 py-3 text-sm font-bold text-primary-400 transition hover:bg-primary-500 hover:text-white"
          >
            Explorer toute la galerie →
          </Link>
        </div>
      )}
    </div>
  );
}
